import { PrivacyPage, TermsPage } from '@dealstack/frontend/shared/pages/Legal/LegalPage';
import { ProtectedRoute } from '@dealstack/frontend/shared/pages/ProtectedRoute/ProtectedRoute';
import { Auth } from '@dealstack/frontend/investor/pages/Auth/Auth';
import {
	DEFAULT_GUEST_ROUTE,
	DEFAULT_PROTECTED_ROUTE,
	ROUTES,
} from '@dealstack/frontend/investor/pages/Routing/routes';
import { InvestmentsDropdown } from '@dealstack/frontend/investor/pages/Valuations/InvestmentsDropdown/InvestmentsDropdown';
import { Navigate, Route } from 'react-router-dom';
import { useAppDispatch } from '@dealstack/frontend/investor/hooks/redux';
import { ApplicationType } from '@dealstack/shared/lib/generated';
import { Investment } from '@dealstack/frontend/investor/pages/Investment/Investment';
import { Page404 } from '@dealstack/frontend/shared/pages/Page404/Page404';
import { SignedRedirect } from '@dealstack/frontend/investor/pages/SignedRedirect/SignedRedirect';
import { Profile } from '@dealstack/frontend/shared/components/Profile/Profile';
import { Documents } from '@dealstack/frontend/investor/pages/Documents/Documents';
import { DocumentsContextProvider } from '@dealstack/frontend/investor/pages/Documents/DocumentsContextProvider';
import { GenericError as GenericErrorPage } from '@dealstack/frontend/shared/pages/GenericError/GenericError';
import { EidEasyFtnCallback } from '@dealstack/frontend/shared/components/EidEasySigningModal/EidEasyFtnCallback';
import { EidEasyOtpCallback } from '@dealstack/frontend/shared/components/EidEasySigningModal/EidEasyOtpCallback';
import { PublicSigningRoute } from '@dealstack/frontend/investor/pages/PublicSigning/PublicSigningRoute';
import { TransactionsList } from '@dealstack/frontend/investor/pages/TransactionsList/TransactionsList';
import { Valuations } from '@dealstack/frontend/investor/pages/Valuations/Valuations';
import { SentryRoutes } from '@dealstack/frontend/shared/sentry';
import { setApplicationType } from '@dealstack/frontend/shared/mechanism/redux/reducers/global/global';
import { HelpBlogBase } from '@dealstack/frontend/app/pages/HelpBlog/HelpBlog.tsx';
import { StandaloneKyc } from '@dealstack/frontend/investor/pages/StandaloneKyc/StandaloneKyc';

interface Props {
	className?: string;
}

export const Routing = ({ className }: Props) => {
	const dispatch = useAppDispatch();
	const applicationType = ApplicationType.INVESTOR;
	dispatch(setApplicationType(applicationType));

	return (
		<div className={className}>
			<SentryRoutes>
				<Route
					path={`${ROUTES.AUTH}/*`}
					element={<Auth applicationType={applicationType} />}
				/>

				<Route
					path={ROUTES.DEFAULT_ROUTE}
					element={
						<ProtectedRoute
							defaultGuestRoute={DEFAULT_GUEST_ROUTE}
							dispatch={dispatch}
							applicationType={applicationType}
							isInvestorApp
							defaultProtectedRoute={DEFAULT_PROTECTED_ROUTE}
							investmentsDropdown={<InvestmentsDropdown />}
						>
							<Valuations />
						</ProtectedRoute>
					}
				>
					<Route
						path={ROUTES.DASHBOARD}
						element={
							<ProtectedRoute
								defaultGuestRoute={DEFAULT_GUEST_ROUTE}
								dispatch={dispatch}
								applicationType={applicationType}
								isInvestorApp
								defaultProtectedRoute={DEFAULT_PROTECTED_ROUTE}
								investmentsDropdown={<InvestmentsDropdown />}
							>
								<Valuations />
							</ProtectedRoute>
						}
					>
						<Route
							index={true}
							element={
								<ProtectedRoute
									defaultGuestRoute={DEFAULT_GUEST_ROUTE}
									dispatch={dispatch}
									applicationType={applicationType}
									isInvestorApp
									defaultProtectedRoute={DEFAULT_PROTECTED_ROUTE}
									investmentsDropdown={<InvestmentsDropdown />}
								>
									<Valuations />
								</ProtectedRoute>
							}
						/>
						<Route
							path=":investorId"
							element={
								<ProtectedRoute
									defaultGuestRoute={DEFAULT_GUEST_ROUTE}
									dispatch={dispatch}
									applicationType={applicationType}
									isInvestorApp
									defaultProtectedRoute={ROUTES.DEFAULT_ROUTE}
									investmentsDropdown={<InvestmentsDropdown />}
								>
									<Valuations />
								</ProtectedRoute>
							}
						/>
					</Route>
				</Route>

				<Route
					path={`${ROUTES.INVESTMENT}/*`}
					element={
						<ProtectedRoute
							defaultGuestRoute={DEFAULT_GUEST_ROUTE}
							dispatch={dispatch}
							applicationType={applicationType}
							isInvestorApp
							defaultProtectedRoute={DEFAULT_PROTECTED_ROUTE}
						>
							<Investment />
						</ProtectedRoute>
					}
				/>

				<Route
					path={`${ROUTES.PROFILE}/*`}
					element={
						<ProtectedRoute
							defaultGuestRoute={DEFAULT_GUEST_ROUTE}
							dispatch={dispatch}
							applicationType={applicationType}
							isInvestorApp
							defaultProtectedRoute={DEFAULT_PROTECTED_ROUTE}
						>
							<Profile
								dashboardRoute={DEFAULT_PROTECTED_ROUTE}
								userProfileRoute={ROUTES.PROFILE}
							/>
						</ProtectedRoute>
					}
				/>

				<Route
					path={`${ROUTES.HELP_PAGE}/*`}
					element={
						<ProtectedRoute
							defaultGuestRoute={DEFAULT_GUEST_ROUTE}
							dispatch={dispatch}
							applicationType={applicationType}
							defaultProtectedRoute={DEFAULT_PROTECTED_ROUTE}
							isInvestorApp
							hideOverflowX={false}
						>
							<HelpBlogBase dataset={ApplicationType.INVESTOR} />
						</ProtectedRoute>
					}
				/>

				<Route
					path={ROUTES.VALUATIONS}
					element={
						<ProtectedRoute
							defaultGuestRoute={DEFAULT_GUEST_ROUTE}
							dispatch={dispatch}
							applicationType={applicationType}
							isInvestorApp
							defaultProtectedRoute={DEFAULT_PROTECTED_ROUTE}
							investmentsDropdown={<InvestmentsDropdown />}
						>
							<Valuations />
						</ProtectedRoute>
					}
				/>

				<Route
					path={ROUTES.TRANSACTIONS}
					element={
						<ProtectedRoute
							defaultGuestRoute={DEFAULT_GUEST_ROUTE}
							dispatch={dispatch}
							applicationType={applicationType}
							isInvestorApp
							defaultProtectedRoute={DEFAULT_PROTECTED_ROUTE}
							investmentsDropdown={<InvestmentsDropdown />}
						>
							<TransactionsList />
						</ProtectedRoute>
					}
				/>

				<Route
					path={ROUTES.TRANSACTIONS_BY_INVESTOR_ID}
					element={
						<ProtectedRoute
							defaultGuestRoute={DEFAULT_GUEST_ROUTE}
							dispatch={dispatch}
							applicationType={applicationType}
							isInvestorApp
							defaultProtectedRoute={DEFAULT_PROTECTED_ROUTE}
							investmentsDropdown={<InvestmentsDropdown />}
						>
							<TransactionsList />
						</ProtectedRoute>
					}
				/>

				<Route
					path={`${ROUTES.DOCUMENTS}/*`}
					element={
						<ProtectedRoute
							defaultGuestRoute={DEFAULT_GUEST_ROUTE}
							dispatch={dispatch}
							applicationType={applicationType}
							isInvestorApp
							defaultProtectedRoute={DEFAULT_PROTECTED_ROUTE}
						>
							<DocumentsContextProvider>
								<Documents />
							</DocumentsContextProvider>
						</ProtectedRoute>
					}
				/>

				<Route
					path={`${ROUTES.STANDALONE_KYC}/*`}
					element={
						<ProtectedRoute
							defaultGuestRoute={DEFAULT_GUEST_ROUTE}
							dispatch={dispatch}
							applicationType={applicationType}
							isInvestorApp
							defaultProtectedRoute={DEFAULT_PROTECTED_ROUTE}
						>
							<StandaloneKyc />
						</ProtectedRoute>
					}
				/>

				<Route path={ROUTES.SIGNED_REDIRECT} element={<SignedRedirect />} />
				<Route path={ROUTES.TERMS} element={<TermsPage />} />
				<Route path={ROUTES.PRIVACY} element={<PrivacyPage />} />
				<Route path={ROUTES.EID_EASY_FTN_CALLBACK} element={<EidEasyFtnCallback />} />
				<Route path={ROUTES.EID_EASY_OTP_CALLBACK} element={<EidEasyOtpCallback />} />
				<Route path={ROUTES.PUBLIC_SIGNING} element={<PublicSigningRoute />} />
				<Route path={ROUTES.PAGE_NOT_FOUND} element={<Page404 />} />
				<Route path={ROUTES.GENERIC_ERROR} element={<GenericErrorPage />} />
				<Route path="*" element={<Navigate to={ROUTES.DASHBOARD} />} />
			</SentryRoutes>
		</div>
	);
};
